import { formatMoneyWithoutTrailingZeros } from './modules/format-money-without-trailing-zeros.js';

if (!customElements.get('product-upsell-products')) {
  class ProductUpsellProducts extends HTMLElement {
    constructor() {
      super();

      // handle click on the upsell options (frames & passe-partouts products)
      this.querySelectorAll('input:not(:disabled').forEach((input) =>
        input.addEventListener('click', this.handleUpsellSelectionChange.bind(this))
      );

      this.init();
    }

    init() {
      // update the upsell options on load if there's info in the sessionStorage that indicates we are in the Edit mode
      const upsellSelectionUpdated = this.updateUpsellSelectionOnLoad();

      this.isPhotoPassePartout = this.dataset.artPrintType == 'photo-passe-partout';

      const setDefaultFrameOnLoad =
        this.dataset.artPrintType == 'mirror' || this.isPhotoPassePartout;
      const setDefaultPhotoPassePartoutOnLoad = this.isPhotoPassePartout;

      // update the upsell frame on load if it's a Mirror or Photo Passe-Partout or Canvas, and the upsell selection was not updated from SessionStorage
      if (!upsellSelectionUpdated) {
        if (setDefaultFrameOnLoad) {
          const defaultFrame = this.querySelector('[data-upsell-default-frame]');
          defaultFrame.checked = true;
          this.handleUpsellSelectionChange.bind(this, { target: defaultFrame })();
        }

        if (setDefaultPhotoPassePartoutOnLoad) {
          const defaultPassePartout = this.querySelector('[data-upsell-default-passe-partout]');
          defaultPassePartout.checked = true;
          this.handleUpsellSelectionChange.bind(this, { target: defaultPassePartout })();
        }

        this.applyCanvasFrameFromCollection();
      }

      // make sure a canvas frame is selected, when re-rendered on the Canvas view (ex: variant change) - Canvas has no "No frame" option
      if (this.closest('product-info')?.classList.contains('frames-secondary-view')) {
        this.handleFrameViewToggle(true);
      }
    }

    /**
     * updateUpsellSelectionOnLoad
     * @description Update the upsell options on load if there's info in the sessionStorage that indicates we are in the Edit mode
     */
    updateUpsellSelectionOnLoad() {
      if (sessionStorage.productEdit == undefined) return;

      const productEditInfo = sessionStorage.productEdit.split('|||');

      // check if there are upsell products selected for the product to be edited
      if (!productEditInfo[2]) return;

      // simulate click on all the inputs of the upsell products that were selected for the product to be edited
      const upsellVariantIds = productEditInfo[2].split(',');
      upsellVariantIds.forEach((upsellVariantId) => {
        const upsellProductInput = this.querySelector(
          `[data-upsell-variant-id="${upsellVariantId}"]`
        );

        if (!upsellProductInput) return;

        upsellProductInput.checked = true;
        this.handleUpsellSelectionChange.bind(this, { target: upsellProductInput })();
      });

      return true;
    }

    /**
     * applyCanvasFrameFromCollection
     * @description `framing=canvas` opens Canvas. `canvas_frame` is the frame that was showing on the card.
     */
    applyCanvasFrameFromCollection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('framing') !== 'canvas') return;

      const frameInput = this.getCanvasFrameInput(params.get('canvas_frame'));
      if (frameInput) {
        frameInput.checked = true;
        this.handleUpsellSelectionChange({ target: frameInput });
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('framing');
      url.searchParams.delete('canvas_frame');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    /**
     * getCanvasFrameInput
     * @description Find the size-matched canvas input for a handle, then the default, then the first canvas - skipping out of stock (disabled) inputs
     * @param {string} frameHandle
     * @returns {HTMLInputElement|null}
     */
    getCanvasFrameInput(frameHandle) {
      const canvasSelector = '[data-upsell-is-canvas]:not(:disabled)';

      return (
        (frameHandle &&
          this.querySelector(`${canvasSelector}[data-upsell-handle="${frameHandle}"]`)) ||
        this.querySelector(`${canvasSelector}[data-upsell-default-frame]`) ||
        this.querySelector(canvasSelector)
      );
    }

    /**
     * handleUpsellSelectionChange
     * @description Handle the change on the upsell options
     */
    handleUpsellSelectionChange(event) {
      this.syncUpsellSelection(event.target);

      // close drawer on option change
      this.closeDrawer();
    }

    /**
     * syncUpsellSelection
     * @description Sync the passe-partout, price and media state to the given upsell option target
     * @param target {HTMLInputElement}: The upsell option input
     * @param isUpsellNone {boolean}: Sync to the "none" state, even if the target isn't the none option (ex: no "No frame" option available)
     */
    syncUpsellSelection(target, isUpsellNone = target.hasAttribute('data-upsell-none')) {
      this.upsellProductInput = target;
      this.upsellType =
        this.upsellProductInput.dataset.upsellProduct == 'frame' ? 'frame' : 'passe-partout';
      this.isUpsellNone = isUpsellNone;
      const isCanvas =
        !this.isUpsellNone && this.upsellProductInput.hasAttribute('data-upsell-is-canvas');

      // show the Canvas frame options list, if a Canvas frame is selected
      if (isCanvas) this.closest('product-info')?.setFrameView?.(true);

      // update upsell drawer trigger content
      this.updateProductMedia(isCanvas);

      // update upsell drawer trigger content
      this.updateProductSummary(isCanvas);
    }

    /**
     * updateProductMedia
     * @description Go to 1st slide and update the product media with the selected frame image/passe-partout classes for the bg-color
     */
    updateProductMedia(isCanvas = false) {
      this.productMediaSlider = document.querySelector('product-media-slider');

      // slide to the 1st slide of the product media slider + show/hide images related to Upsell frames
      this.productMediaSlider?.goToSlide(0);
      if (this.productMediaSlider?.querySelector('[data-upsell-image]')) {
        this.productMediaSlider.showUpsellSlides(isCanvas || this.isPhotoPassePartout);

        if (isCanvas || this.isPhotoPassePartout) this.updateUpsellImages();
      }

      // get the elements/product media for this upsell type and shadow
      const productMediaWrapper = this.productMediaSlider.querySelector(
        '[data-product-media-images-wrapper]'
      );
      const productMediaUpsell =
        this.upsellType == 'frame'
          ? this.productMediaSlider.querySelector('.product-media-slider__frame')
          : this.productMediaSlider.querySelector('.product-media-slider__passe-partout');

      if (!productMediaUpsell) return;

      const productMediaPassePartout = this.productMediaSlider.querySelector(
        '.product-media-slider__passe-partout'
      );
      const productMediaShadow = this.productMediaSlider.querySelector(
        '.product-media-slider__shadow'
      );

      // hide the upsell image if no option is selected
      productMediaUpsell?.classList.toggle('opacity-0', this.isUpsellNone);

      // check if a frame or passe-partout is selected
      const isProductFrameSelected = Array.from(this.querySelectorAll('input[name="frame"]')).find(
        (radio) => radio.checked && !radio.hasAttribute('data-upsell-none')
      );
      const isProductPassePartoutSelected = Array.from(
        this.querySelectorAll('input[name="passe-partout"]')
      ).find((radio) => radio.checked && !radio.hasAttribute('data-upsell-none'));

      // hide the passe-partout image, if no frame is selected or if it's a canvas frame
      const showPassePartout =
        isProductFrameSelected != undefined &&
        isProductPassePartoutSelected != undefined &&
        !isCanvas
          ? true
          : false;
      productMediaPassePartout?.classList.toggle('opacity-0', !showPassePartout);

      // set class when a passe-partout is selected (for the padding)
      productMediaWrapper?.classList.toggle(
        'product-media-slider--has-passe-partout',
        showPassePartout
      );

      // hide the shadow if a frame is selected
      productMediaShadow?.classList.toggle('opacity-0', isProductFrameSelected != undefined);

      // return if no upsell option is selected
      if (this.isUpsellNone) return;

      // get the upsell image from the selected option and update the product media with it
      const upsellOverlayImage = this.upsellProductInput.dataset.upsellImageOverlay;

      // update the upsell image on the product media
      productMediaUpsell.srcset = upsellOverlayImage;

      // retrigger the shimmer effect for mirror products
      if (this.upsellType == 'frame') this.retriggerMirrorShimmerAnimation();
    }

    /**
     * retriggerMirrorShimmerAnimation
     * @description Retrigger the shimmer effect for mirror products on Frame change
     * @param {Object} mirrorElemWrapper
     */
    retriggerMirrorShimmerAnimation() {
      const mirrorElemWrapper = this.productMediaSlider.querySelector(
        '[data-mirror-shimmer-effect]'
      );
      if (!mirrorElemWrapper) return;

      mirrorElemWrapper.classList.remove('before:animate-[shimmer_4s_0.4s_1]');
      void mirrorElemWrapper.offsetWidth; // force reflow to trigger the animation
      mirrorElemWrapper.classList.add('before:animate-[shimmer_4s_0.4s_1]');
    }

    /**
     * updateUpsellImages
     * @description Take Upsell images from the Upsell frame trigger, and set them as the Upsell images in the Gallery
     */
    updateUpsellImages() {
      this.productMediaSlider.isCanvasOrPpp = true;

      const srcDataset = this.upsellProductInput.dataset;
      const upsellImageElements =
        this.productMediaSlider.querySelectorAll('[data-upsell-image] img');

      upsellImageElements.forEach((image, index) => {
        const imageSrc = srcDataset[`upsellImage-${index + 1}`];
        if (imageSrc) image.srcset = imageSrc;
      });
    }

    /**
     * updateProductSummary
     * @description Update the product summary with the selected upsell product
     * @param isCanvas {boolean}: Is the selected upsell product a canvas frame
     */
    updateProductSummary(isCanvas = false) {
      // get the passe-partout trigger
      const upsellDrawerPassePartoutTrigger = this.upsellProductInput
        .closest('[data-upsell-products-selector]')
        .querySelector('[data-toggle-passe-partout]');

      // get the trigger element type of this event (frame or passe-partout)
      const upsellDrawerTrigger =
        this.upsellType == 'frame'
          ? this.upsellProductInput
              .closest('[data-upsell-products-selector]')
              .querySelector('[data-toggle-frame]')
          : upsellDrawerPassePartoutTrigger;

      // get the default (select & x options available) and selected product (img, title & price) trigger elements
      const upsellDrawerTriggerDefault = upsellDrawerTrigger?.querySelector(
        '[data-current-upsell-product-default]'
      );
      const upsellDrawerTriggerProduct = upsellDrawerTrigger?.querySelector(
        '[data-current-upsell-product]'
      );

      // hide the default trigger info and show the upsell drawer trigger with the current selection
      upsellDrawerTriggerDefault?.classList.add('hidden');
      upsellDrawerTriggerProduct?.classList.remove('hidden');

      // show the passe-partout trigger, if it's a frame upsell and there are passe-partout options available
      upsellDrawerPassePartoutTrigger?.classList.toggle(
        'hidden',
        this.upsellType == 'frame' &&
          this.isUpsellNone &&
          upsellDrawerPassePartoutTrigger?.classList.contains('no-passe-partout-options-available')
      );

      // hide the Passe-partout trigger, if the frame is set to None or it's a canvas frame
      if (this.upsellType == 'frame' && (this.isUpsellNone || isCanvas)) {
        upsellDrawerPassePartoutTrigger?.classList.add('hidden');

        // show the default trigger info (Select & X options available) and hide the current selection info
        upsellDrawerPassePartoutTrigger
          ?.querySelector('[data-current-upsell-product-default]')
          .classList.remove('hidden');
        upsellDrawerPassePartoutTrigger
          ?.querySelector('[data-current-upsell-product]')
          .classList.add('hidden');

        // reset the passe-partout selection
        const selectedPassePartout = Array.from(
          this.querySelectorAll('[data-upsell-product="passe-partout"]')
        ).filter((radio) => radio.checked);
        if (selectedPassePartout[0]) selectedPassePartout[0].checked = false;
      }

      // Update the price sum on the Summary & Add to cart button
      this.updatePrices();

      // Update the Selected upsell product info on the trigger

      if (!upsellDrawerTriggerProduct) return;

      // update the image visibility on the drawer trigger (product image or none image)
      const noUpsellDrawerTriggerImage =
        upsellDrawerTriggerProduct.querySelector('[data-no-upsell-image]');
      noUpsellDrawerTriggerImage?.classList.toggle('hidden', !this.isUpsellNone);

      const upsellDrawerTriggerImage = upsellDrawerTriggerProduct.querySelector('img');
      upsellDrawerTriggerImage?.classList.toggle('hidden', this.isUpsellNone);
      // update image
      if (!this.isUpsellNone) {
        const upsellProductPreviewImage = this.upsellProductInput.dataset.upsellPreviewImage;
        upsellDrawerTriggerImage.srcset = upsellProductPreviewImage;
      }

      // update title
      const upsellProductTitle = this.upsellProductInput.dataset.upsellTitle;
      upsellDrawerTriggerProduct.querySelector('[data-current-upsell-title]').innerHTML =
        upsellProductTitle;

      // update price - if it's the none option, don't show the price
      const upsellProductPrice = this.upsellProductInput.dataset.upsellFormattedPrice;
      const upsellDrawerTriggerPrice = upsellDrawerTriggerProduct.querySelector(
        '[data-current-upsell-price]'
      );
      if (upsellDrawerTriggerPrice)
        upsellDrawerTriggerPrice.innerHTML = !this.isUpsellNone ? upsellProductPrice : '';
    }

    /**
     * closeDrawer
     * @description Close drawer for each option
     */
    closeDrawer() {
      const body = document.querySelector('body');
      body.classList.remove(
        'upsell-products-1',
        'upsell-products-2',
        'upsell-products-drawer-is-open'
      );
    }

    /**
     * updatePrices
     * @description Update the Product Summary and Add to cart button prices (total & compare at price) with the selected upsell products
     * @returns {void}
     */
    updatePrices() {
      // get the total & compare at Price destination elements in the Summary and ATC button
      const productSummaryTotalPriceDestination = document.querySelector(
        '[data-summary-total-price]'
      );
      const productSummaryComparePriceDestination = document.querySelector(
        '[data-summary-compare-price]'
      );
      const addToCartPriceDestination = document.querySelector('[data-add-to-cart-button-price]');

      if (!productSummaryTotalPriceDestination && !addToCartPriceDestination) return;

      // get the product total price
      const totalProductPrice = +document.querySelector('[data-total-price]').dataset.totalPrice;

      // get the upsell product prices
      const selectedUpsellProducts = Array.from(
        this.querySelectorAll('[data-upsell-product]:not([data-upsell-none])')
      ).filter((radio) => radio.checked);
      const selectedUpsellProductPrices = selectedUpsellProducts.map(
        (price) => +price.dataset.upsellPrice
      );

      // sum up the main product price and all the upsell product prices
      const totalPrice = selectedUpsellProductPrices.reduce(
        (total, currentUpsellPrice) => total + currentUpsellPrice,
        totalProductPrice
      );

      const finalPrice = formatMoneyWithoutTrailingZeros(totalPrice);

      // update the Product summary and Add to cart prices
      productSummaryTotalPriceDestination.innerHTML = finalPrice;
      addToCartPriceDestination.innerHTML = finalPrice;

      // update the Compare at price
      if (!productSummaryComparePriceDestination) return;

      // get the product compare at price
      const productComparePrice =
        +document.querySelector('[data-compare-price]').dataset.comparePrice;

      // get the upsell product compare at prices
      const selectedUpsellProductComparePrices = selectedUpsellProducts.map(
        (price) => +price.dataset.upsellComparePrice
      );
      // sum up the compare at price and all the upsell product compare at prices
      let totalComparePrice = selectedUpsellProductComparePrices.reduce(
        (total, currentUpsellComparePrice) => total + currentUpsellComparePrice,
        productComparePrice
      );

      // empty the compare at price element in the Summary and return, if the main and upsell products have no compare at price
      if (totalComparePrice == 0) {
        productSummaryComparePriceDestination.innerHTML = '';
        return;
      }

      // get the final price of the main product, in case the upsell products have compare prices and we need to calculate the final Compare price with it
      let mainProductWithoutComparePriceTotal = productComparePrice == 0 ? totalProductPrice : 0;

      // get the final price of the extra items that don't have a compare price
      const upsellProductWithoutComparePrices = selectedUpsellProducts.map((price) => {
        return price.dataset.upsellComparePrice == '' ? +price.dataset.upsellPrice : 0;
      });
      // sum up the main product price and all the upsell products that don't have compare prices
      const allProductWithoutComparePricesTotal = upsellProductWithoutComparePrices.reduce(
        (total, currentUpsellPriceWithoutCompare) => total + currentUpsellPriceWithoutCompare,
        mainProductWithoutComparePriceTotal
      );

      // sum up the compare prices with the products without compare prices
      totalComparePrice = totalComparePrice + allProductWithoutComparePricesTotal;

      const comparePrice = formatMoneyWithoutTrailingZeros(totalComparePrice);

      // update the Product summary compare at price
      productSummaryComparePriceDestination.innerHTML = comparePrice;
    }

    /**
     * handleFrameViewToggle
     * @description Called by <product-info>, which owns the toggle buttons.
     * Canvas has no "No frame" option, so switching to Canvas selects the default canvas frame.
     */
    handleFrameViewToggle(isCanvasView) {
      const selectedFrame = this.querySelector('input[name="frame"]:checked');
      const selectedFrameIsCanvas = selectedFrame?.hasAttribute('data-upsell-is-canvas') ?? false;

      if (selectedFrameIsCanvas == isCanvasView && (selectedFrame || !isCanvasView)) return;

      const canvasFrameInput = isCanvasView && this.getCanvasFrameInput();
      if (canvasFrameInput) {
        canvasFrameInput.checked = true;
        this.syncUpsellSelection(canvasFrameInput);
        return;
      }

      // no canvas frame available (or back to Art Print): unselect the previous frame
      if (!selectedFrame) return;
      selectedFrame.checked = false;
      this.resetFrameToUnselectedState(selectedFrame);
    }

    /**
     * resetFrameToUnselectedState
     * @param previousFrame {HTMLInputElement}: The frame input that was unselected
     */
    resetFrameToUnselectedState(previousFrame) {
      // the "No frame" option is optional, so fall back to the unselected frame input, synced as a "none" selection
      const noFrameInput =
        this.querySelector('[data-upsell-product="frame"][data-upsell-none]') || previousFrame;
      if (!noFrameInput) return;

      // sync the passe-partout, price and media logic to the "no frame" state directly, without
      // marking any frame option as selected or triggering click-only side effects (e.g. closing the drawer)
      this.syncUpsellSelection(noFrameInput, true);

      // show the default trigger info (Select & X options available) and hide the current selection info
      const upsellDrawerTrigger = this.closest('[data-upsell-products-selector]')?.querySelector(
        '[data-toggle-frame]'
      );
      upsellDrawerTrigger
        ?.querySelector('[data-current-upsell-product-default]')
        ?.classList.remove('hidden');
      upsellDrawerTrigger?.querySelector('[data-current-upsell-product]')?.classList.add('hidden');
    }
  }
  customElements.define('product-upsell-products', ProductUpsellProducts);
}
